/**
 * Resolves the primary and secondary CTA hrefs used across the site.
 *
 * Resolution order for primaryHref:
 *   1. portal_url            — set by backend when account has consumer portal enabled
 *   2. external_management_url — booking platform URL from the API
 *   3. /contact               — final fallback
 *
 * No per-site configuration needed. Enable the portal in the admin console and
 * every CTA across the site automatically routes to the portal.
 */

import type { CompanyInformation } from '../types/api/company-information';

const CONTACT_PATH = '/contact';

/** True when the href is absolute (external); use for target="_blank" and rel="noopener noreferrer". */
export function isExternalCtaUrl(href: string): boolean {
  return href.startsWith('http://') || href.startsWith('https://');
}

export interface ResolvedCtaUrls {
  primaryHref: string;
  secondaryHref: string;
  hasSecondary: boolean;
}

/**
 * Extracts the pathname from portal_url for use in client-side route comparisons.
 * Handles both absolute URLs (https://domain.com/portal → /portal) and relative paths (/portal).
 *
 * Returns null when:
 *  - portal_url is not set
 *  - the URL cannot be resolved to a non-root pathname (would otherwise match every page)
 */
export function resolvePortalPath(
  companyInformation?: CompanyInformation | null
): string | null {
  const url = companyInformation?.portal_url?.trim();
  if (!url) return null;

  let pathname: string | null = null;
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = url.startsWith('/') ? url : null;
  }

  if (!pathname || pathname === '/') return null;

  // Normalize trailing slash so startsWith comparisons are consistent.
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function resolveCtaUrls(
  companyInformation?: CompanyInformation | null
): ResolvedCtaUrls {
  const portalUrl = companyInformation?.portal_url?.trim() || null;
  const externalUrl = companyInformation?.external_management_url?.trim() || null;
  const primaryHref = portalUrl ?? externalUrl ?? CONTACT_PATH;
  const secondaryHref = CONTACT_PATH;
  const hasSecondary = primaryHref !== CONTACT_PATH;

  return { primaryHref, secondaryHref, hasSecondary };
}
